/**
 * Granny.
 *
 * Composition: SensorMemory + RoutineScheduler + StateMachine, exactly the
 * pattern every other character reuses (dog, rival cat). The state machine is
 * flat and its transition table is declared as data at the bottom of this file -
 * start there if you want to understand her behavior.
 *
 * She also owns her view (mesh + vision cone). The cone is not a debug aid, it
 * is a CORE FEATURE: a 6-year-old cannot infer a field-of-view from an animation,
 * so we draw it on the floor and change its color by tier. Real stealth systems,
 * cartoon legibility.
 */

import * as THREE from 'three';
import type RAPIER from '@dimforge/rapier3d-compat';
import type { ActivityDef } from '@/levels/schema';
import type { EventBus, HeatTier, NoiseEvent } from '@/core/events';
import type { Vec3 } from '@/core/math';
import { clone, DEG_TO_RAD, distanceXZ, rotateToward, vec3, yawTowards } from '@/core/math';
import { tuning } from '@/core/tuning';
import type { Rng } from '@/core/rng';
import type { PhysicsWorld } from '@/engine/physicsWorld';
import { createAssetObject } from '@/engine/assetRegistry';
import { addOutlinesRecursive } from '@/render/outline';
import { StateMachine, type State, type Transition } from './stateMachine';
import {
  presentationFor,
  RoutineScheduler,
  type ActivityPresentation,
  type ActivityProfile,
} from './routine';
import {
  canHear,
  catTargetPoints,
  clearNoiseMemory,
  computeVision,
  createSensorMemory,
  markHideSpotChecked,
  nextHideSpotToCheck,
  perceivedLoudness,
  rememberNoise,
  rememberSighting,
  stepMemory,
  type SensorMemory,
} from './sensors';
import { addHeat, speedForTier, stepContact, type HeatState } from '@/systems/heat';
import {
  findTrace,
  nearestUnresolvedTrace,
  tidyTrace,
  tracesInDiscoveryRange,
  type Trace,
  type TraceStore,
} from '@/systems/traces';

/** Everything granny needs to know about the cat, sampled once per step. */
export interface CatSnapshot {
  position: Vec3;
  headPosition: Vec3;
  height: number;
  crouching: boolean;
  moving: boolean;
  /** Hide spot the cat is currently inside, or null. */
  hideSpotId: string | null;
  /** Inside a hide spot AND granny did not watch it get in. */
  concealed: boolean;
}

export interface HideSpotRef {
  id: string;
  position: Vec3;
  size: Vec3;
}

export interface GrannyDeps {
  physics: PhysicsWorld;
  scene: THREE.Scene;
  heat: HeatState;
  traces: TraceStore;
  bus: EventBus;
  rng: Rng;
  hideSpots: HideSpotRef[];
  getCat: () => CatSnapshot;
  roomIdAt: (x: number, z: number) => string | undefined;
  /** Volumes only the cat may traverse. Granny steers around these. */
  catOnlyVolumes: { center: Vec3; size: Vec3 }[];
}

export const GRANNY_STATES = [
  'routine',
  'pet',
  'alert',
  'investigate',
  'search',
  'chase',
  'spray',
  'catch',
  'recover',
] as const;

export type GrannyStateName = (typeof GRANNY_STATES)[number];

export class Granny {
  readonly position: Vec3;
  yaw = 0;

  readonly memory: SensorMemory = createSensorMemory();
  readonly routine: RoutineScheduler;
  readonly machine: StateMachine<Granny>;

  /** Perception results for this step, recomputed at the top of `step`. */
  canSeeCat = false;
  catDistance = Infinity;

  /** Where she is currently heading. Null means stand still. */
  moveTarget: Vec3 | null = null;

  private body: RAPIER.RigidBody;
  private collider: RAPIER.Collider;
  private controller: RAPIER.KinematicCharacterController;

  private sprayCooldown = 0;
  private sprintTime = 0;
  exhausted = false;

  /** Reaction delay left before she can act on a stimulus. */
  private interruptDelay = 0;
  private pendingStimulus = false;

  private investigateTarget: Trace | null = null;
  /**
   * Where she committed to look, snapshotted when she reacted.
   *
   * Deliberately NOT re-read from memory every step. It used to be, and since the
   * cat emits a noise event every fixed step, the "place I heard something" was
   * continuously overwritten with wherever the cat currently was - so investigate
   * silently became a homing chase at walking pace, at any tier, forever.
   */
  private investigateAnchor: Vec3 | null = null;
  private investigateElapsed = 0;
  private searchTarget: HideSpotRef | null = null;
  private searchTargetElapsed = 0;
  /** Loudest cat movement heard this step, consumed by the heat trickle. */
  private heardCatLoudness = 0;

  /** Counts down at all tiers; she may only offer a fuss when it reaches zero. */
  private petCooldown = tuning.granny.pet.cooldownSeconds;
  private petElapsed = 0;
  private petDwell = 0;

  /** Set while she is walking a caught cat back to its bed. */
  private carryDestination: Vec3 | null = null;
  private carryElapsed = 0;
  private carryBestDistance = Infinity;
  private carryStuckFor = 0;
  private carrySide = 1;

  /** Yaw she was holding when she stopped to look around, so the sweep is centred. */
  private alertBaseYaw = 0;

  readonly object: THREE.Object3D;
  private visionCone: THREE.Mesh;
  private coneMaterial: THREE.MeshBasicMaterial;
  private bubble: THREE.Mesh;
  private unsubscribeNoise: () => void;

  constructor(
    private readonly deps: GrannyDeps,
    spawn: Vec3,
    routineDefs: ActivityDef[],
  ) {
    this.position = clone(spawn);
    this.routine = new RoutineScheduler(routineDefs, deps.rng);

    const created = deps.physics.addCharacterCapsule({
      position: spawn,
      radius: tuning.granny.radius,
      height: tuning.granny.height,
      isCat: false,
    });
    this.body = created.body;
    this.collider = created.collider;
    this.controller = deps.physics.createCharacterController(0.03);

    // View
    this.object = new THREE.Object3D();
    this.object.name = 'granny';
    const mesh = createAssetObject('char.granny');
    // 'character' keeps her outline even on the low quality tier - she is the
    // thing the player most needs to read clearly at a glance.
    addOutlinesRecursive(mesh, { outlineClass: 'character' });
    this.object.add(mesh);

    const bottle = createAssetObject('prop.spray_bottle');
    // Held out to the side and slightly BEHIND her shoulder line, so it reads as
    // a held object rather than as a face. On the plain capsule placeholder a
    // bottle sitting forward of centre was the most face-like thing on her, and
    // players read it as a head turned sideways.
    bottle.position.set(0.3, 0.7, 0.16);
    addOutlinesRecursive(bottle, { outlineClass: 'character' });
    this.object.add(bottle);

    // Until she has a real model there is nothing on the capsule to say which way
    // she is pointing, and "which way is granny looking" is the single most
    // important thing to read in a stealth game. One merged mesh, one material,
    // one draw call - see AGENTS.md for the budget.
    this.object.add(buildFaceMarker());

    const cone = buildVisionCone(tuning.granny.vision.fovDeg, tuning.granny.vision.range);
    this.coneMaterial = cone.material;
    this.visionCone = cone.mesh;
    deps.scene.add(this.visionCone);

    this.bubble = buildPersonalSpaceRing(tuning.granny.vision.personalSpaceRadius);
    deps.scene.add(this.bubble);

    deps.scene.add(this.object);

    this.machine = new StateMachine<Granny>(buildStates(), buildTransitions(), 'routine');
    this.machine.start(this);

    this.unsubscribeNoise = deps.bus.on('noise', this.onNoise);
  }

  /* ---------------- derived state, read by transitions ---------------- */

  get tier(): HeatTier {
    return this.deps.heat.tier;
  }

  get spotted(): boolean {
    return this.deps.heat.spotted;
  }

  get isAggressive(): boolean {
    return this.tier === 'hunting' || this.tier === 'frenzy';
  }

  /**
   * She has a fix on the cat right now.
   *
   * Normally that means Contact is full - roughly a second of unbroken line of
   * sight. At Frenzy she needs no build-up at all: DESIGN.md's top tier is a
   * cartoon meltdown, and "she just comes straight at you until you are hidden"
   * is the whole point of letting heat get that high.
   */
  get hasFix(): boolean {
    return this.spotted || (this.tier === 'frenzy' && this.canSeeCat);
  }

  get canSpray(): boolean {
    return this.sprayCooldown <= 0;
  }

  get eyePosition(): Vec3 {
    return vec3(this.position.x, this.position.y + tuning.granny.vision.eyeHeight, this.position.z);
  }

  /** Which room she is standing in. Drives the "unresolved trace here" check. */
  get roomId(): string {
    return this.deps.roomIdAt(this.position.x, this.position.z) ?? 'unknown';
  }

  /**
   * Perception scaling from her current activity. Only applies while she is in
   * her routine - once she is hunting she is fully alert regardless of what she
   * was doing. This is why the VR window closes the instant she is alarmed.
   */
  private get activeProfile(): ActivityProfile {
    if (this.machine.current === 'pet') return PETTING_PROFILE;
    if (this.machine.current !== 'routine') return ALERTED_PROFILE;
    return this.routine.currentProfile;
  }

  /**
   * What she is doing, for the on-screen chip.
   *
   * Derived from the SAME profile perception uses, so the chip cannot disagree
   * with what she can actually see and hear. Off-routine this is the substituted
   * 'alerted' profile, which is why an interrupted granny reads as stopped
   * rather than as still watering her plants.
   */
  get presentation(): ActivityPresentation {
    return presentationFor(this.activeProfile);
  }

  /* ---------------------------- main step ---------------------------- */

  step(dt: number): void {
    if (this.carryDestination) {
      this.stepCarry(dt);
      return;
    }

    const heat = this.deps.heat;
    const cat = this.deps.getCat();
    const profile = this.activeProfile;

    this.sprayCooldown = Math.max(0, this.sprayCooldown - dt);
    this.searchTargetElapsed += dt;
    this.investigateElapsed += dt;
    this.petCooldown = Math.max(0, this.petCooldown - dt);
    this.applyMovementNoiseHeat(dt);
    stepMemory(this.memory, dt);

    // --- Vision -------------------------------------------------------
    const vision = computeVision({
      eye: this.eyePosition,
      yaw: this.yaw,
      targetPoints: catTargetPoints(cat.position, cat.height, cat.headPosition),
      hasLineOfSight: (from, to) => this.deps.physics.hasLineOfSight(from, to),
      fovDeg: tuning.granny.vision.fovDeg * profile.visionFovScale,
      range: tuning.granny.vision.range * profile.visionRangeScale,
      // A blind activity (VR, napping) also switches off personal space; being
      // bumped is handled as an explicit mischief action instead.
      personalSpaceRadius:
        profile.visionFovScale > 0 ? tuning.granny.vision.personalSpaceRadius : 0,
    });

    // Hiding inside a spot she has not seen you enter defeats vision outright.
    this.canSeeCat = vision.visible && !cat.concealed;
    this.catDistance = vision.distance;
    this.applySightHeat(dt);

    // --- Contact ------------------------------------------------------
    const edge = stepContact(
      heat,
      {
        observed: this.canSeeCat,
        distance: vision.distance,
        crouching: cat.crouching,
        moving: cat.moving,
        concealed: cat.concealed,
      },
      dt,
    );

    if (this.canSeeCat) rememberSighting(this.memory, cat.position);
    if (edge.justSpotted) {
      this.deps.bus.emit('cat:spotted', { by: 'granny', position: clone(cat.position) });
      // At Standby the sight of the cat is not a stimulus. DESIGN.md's Standby
      // row is "runs her routine, hums, suspects nothing" - and reacting to her
      // own cat walking past was half of why she trailed the player around a
      // house where nothing had happened. She may still wander over for a fuss;
      // see the `pet` state.
      if (this.tier !== 'standby') {
        this.pendingStimulus = true;
        this.interruptDelay = profile.interruptDelay;
      }
    }
    if (edge.justLost && this.memory.lastKnownPosition) {
      this.deps.bus.emit('cat:lost', {
        by: 'granny',
        lastKnown: clone(this.memory.lastKnownPosition),
      });
    }

    // --- Trace discovery ----------------------------------------------
    const discovered = tracesInDiscoveryRange(this.deps.traces, this.eyePosition, (to) =>
      this.deps.physics.hasLineOfSight(this.eyePosition, to),
    );
    for (const trace of discovered) {
      const gained = findTrace(trace);
      if (gained > 0) {
        addHeat(heat, gained);
        this.deps.bus.emit('trace:found', { traceId: trace.id });
        this.deps.bus.emit('heat:changed', {
          heat: heat.heat,
          delta: gained,
          reason: `found:${trace.kind}`,
        });
        this.pendingStimulus = true;
        this.interruptDelay = profile.interruptDelay;
      }
    }

    // --- Reaction delay -----------------------------------------------
    // The delay gates only the TRANSITION into aggression, not perception, so
    // Contact still fills while she is "processing". That is what makes the VR
    // grace period feel like a window rather than a cheat.
    if (this.interruptDelay > 0) this.interruptDelay = Math.max(0, this.interruptDelay - dt);

    // --- Frenzy stamina ----------------------------------------------
    // The balance valve: 8s of sprinting, then she gasses out for 4s.
    if (this.tier === 'frenzy' && this.moveTarget && !this.exhausted) {
      this.sprintTime += dt;
      if (this.sprintTime >= tuning.granny.frenzy.staminaSeconds) this.exhausted = true;
    } else if (this.tier !== 'frenzy') {
      this.sprintTime = Math.max(0, this.sprintTime - dt * 0.5);
    }

    // --- Behavior ----------------------------------------------------
    this.moveTarget = null;
    this.machine.step(this, dt);
    if (this.machine.lastChange) {
      this.deps.bus.emit('granny:state', this.machine.lastChange);
    }

    // --- Movement ----------------------------------------------------
    this.applyMovement(dt);
    this.updateView();
  }

  /** True once the reaction delay has elapsed and she may act. */
  get readyToReact(): boolean {
    return this.pendingStimulus && this.interruptDelay <= 0;
  }

  clearStimulus(): void {
    this.pendingStimulus = false;
  }

  /** Centres the look-around sweep and consumes the stimulus she just reacted to. */
  beginAlert(): void {
    this.alertBaseYaw = this.yaw;
    this.pendingStimulus = false;
  }

  /* ---------------------------- movement ---------------------------- */

  private applyMovement(dt: number, speedOverride?: number): void {
    const speed = speedOverride ?? (this.exhausted ? 0 : speedForTier(this.tier));

    if (!this.moveTarget || speed <= 0) {
      // Standing still: still face where the behavior wants her looking.
      this.faceTarget(dt);
      return;
    }

    const toTarget = vec3(
      this.moveTarget.x - this.position.x,
      0,
      this.moveTarget.z - this.position.z,
    );
    const dist = Math.hypot(toTarget.x, toTarget.z);
    if (dist < 0.05) {
      this.faceTarget(dt);
      return;
    }

    let dirX = toTarget.x / dist;
    let dirZ = toTarget.z / dist;

    // Steer around cat-only volumes. The cat can dive under the couch; granny
    // has to go around, which is the whole point of the mechanic.
    const avoided = this.avoidCatOnly(dirX, dirZ, speed * dt);
    dirX = avoided.x;
    dirZ = avoided.z;

    const desired = {
      x: dirX * speed * dt,
      y: tuning.sim.gravity * dt * dt,
      z: dirZ * speed * dt,
    };

    this.controller.computeColliderMovement(this.collider, desired);
    const corrected = this.controller.computedMovement();
    const translation = this.body.translation();
    const next = {
      x: translation.x + corrected.x,
      y: translation.y + corrected.y,
      z: translation.z + corrected.z,
    };
    this.body.setNextKinematicTranslation(next);

    this.position.x = next.x;
    this.position.y = next.y - tuning.granny.height / 2;
    this.position.z = next.z;

    // Face the direction of travel.
    const targetYaw = Math.atan2(-dirX, -dirZ);
    this.yaw = rotateToward(this.yaw, targetYaw, tuning.granny.turnRateDeg * DEG_TO_RAD * dt);
  }

  /** Faces the cat when aggressive, otherwise whatever the routine dictates. */
  private faceTarget(dt: number): void {
    const rate = tuning.granny.turnRateDeg * DEG_TO_RAD * dt;
    if (this.isAggressive && this.memory.lastKnownPosition) {
      this.yaw = rotateToward(
        this.yaw,
        yawTowards(this.position, this.memory.lastKnownPosition),
        rate,
      );
      return;
    }
    if (this.machine.current === 'pet') {
      // Looking at the cat she is fussing over, not at the plant behind it.
      this.yaw = rotateToward(
        this.yaw,
        yawTowards(this.position, this.deps.getCat().position),
        rate,
      );
      return;
    }
    if (this.machine.current === 'alert') {
      // The "?" beat. She scans the room instead of freezing mid-stride facing
      // her pot plant - DESIGN.md's Suspicious row is "pauses, looks around",
      // and a granny who stops dead still pointing the same way reads as the
      // game having hitched rather than as a warning shot.
      const look = tuning.granny.investigate;
      const sweep =
        Math.sin(this.machine.timeInState * look.lookSweepHz * Math.PI * 2) *
        look.lookSweepArcDeg *
        DEG_TO_RAD;
      this.yaw = rotateToward(this.yaw, this.alertBaseYaw + sweep, rate);
      return;
    }
    if (this.machine.current === 'routine') {
      const target = this.routine.facingYaw(this.position, this.routine.timeInActivity);
      this.yaw = rotateToward(this.yaw, target, rate);
    }
  }

  /**
   * Nudges a desired direction away from cat-only volumes.
   * Tries progressively wider deflections; gives up and stops rather than
   * walking into one, which reads as "she can't fit" instead of a stuck AI.
   */
  private avoidCatOnly(dirX: number, dirZ: number, stepLength: number): { x: number; z: number } {
    if (this.deps.catOnlyVolumes.length === 0) return { x: dirX, z: dirZ };

    const wouldEnter = (dx: number, dz: number): boolean => {
      const px = this.position.x + dx * (stepLength + tuning.granny.radius);
      const pz = this.position.z + dz * (stepLength + tuning.granny.radius);
      return this.deps.catOnlyVolumes.some(
        (volume) =>
          Math.abs(px - volume.center.x) <= volume.size.x / 2 + tuning.granny.radius &&
          Math.abs(pz - volume.center.z) <= volume.size.z / 2 + tuning.granny.radius,
      );
    };

    if (!wouldEnter(dirX, dirZ)) return { x: dirX, z: dirZ };

    for (const deflection of [0.6, -0.6, 1.2, -1.2, 1.9, -1.9]) {
      const cos = Math.cos(deflection);
      const sin = Math.sin(deflection);
      const nx = dirX * cos - dirZ * sin;
      const nz = dirX * sin + dirZ * cos;
      if (!wouldEnter(nx, nz)) return { x: nx, z: nz };
    }
    return { x: 0, z: 0 };
  }

  /* ---------------------------- hearing ---------------------------- */

  private onNoise = (event: NoiseEvent): void => {
    if (event.source === 'granny') return;

    const dist = distanceXZ(this.eyePosition, event.position);
    const walls = this.deps.physics.countWallsBetween(
      this.eyePosition,
      event.position,
      tuning.granny.hearing.maxWalls,
    );
    const perceived =
      perceivedLoudness({ loudness: event.loudness, distance: dist, wallCount: walls }) *
      this.activeProfile.hearingScale;

    if (!canHear(perceived)) return;

    rememberNoise(this.memory, event.position, perceived);

    // Movement noise is banked here and charged per SECOND in step(), not per
    // event: the cat emits a noise event every fixed step, so scoring it here
    // multiplied the intended trickle by 60 and merely walking near her was
    // worth 6.4 heat/second. Mischief noise never adds heat at all -
    // resolveMischief already scored it, and double-counting would make loud
    // breakables absurdly punishing.
    if (event.source === 'cat') {
      this.heardCatLoudness = Math.max(this.heardCatLoudness, perceived);
    }

    // Hearing something and caring about it are different questions. A calm
    // granny hears the cat padding around her own living room and carries on
    // watering her plants; a shattering vase gets her up. The bar drops as she
    // gets jumpier, so by Hunting she reacts to everything.
    if (perceived < tuning.granny.hearing.investigateLoudness[this.deps.heat.tier]) return;
    this.pendingStimulus = true;
    if (this.interruptDelay <= 0) this.interruptDelay = this.activeProfile.interruptDelay;
  };

  /**
   * Charges the movement-noise trickle for one step, then clears the bank.
   *
   * Scaled to zero at standby on purpose. A cat walking around its own house is
   * not a crime, DESIGN.md's heat table lists no source of alarm except mischief
   * and traces, and §6 promises that sometimes you just get to be a cat. Once she
   * is already suspicious, noise compounds and running past her costs real heat.
   */
  private applyMovementNoiseHeat(dt: number): void {
    const loudness = this.heardCatLoudness;
    this.heardCatLoudness = 0;
    if (loudness <= 0) return;

    const hearing = tuning.granny.hearing;
    const scale = hearing.movementHeatTierScale[this.deps.heat.tier];
    if (scale <= 0) return;
    addHeat(this.deps.heat, loudness * hearing.movementHeatPerSecond * scale * dt);
  }

  /**
   * Heat for being seen, once she is already alarmed.
   *
   * The escalation the tier table always implied but nothing implemented: at
   * Suspicious she is looking for the culprit, and finding a cat standing in the
   * wreckage is what turns "something happened" into "it was YOU". Zero at
   * standby for the same reason as the noise trickle - see DESIGN.md 1.6.
   * Contact, which is a separate meter entirely, still fills at every tier.
   */
  private applySightHeat(dt: number): void {
    if (!this.canSeeCat) return;
    const vision = tuning.granny.vision;
    const scale = vision.sightHeatTierScale[this.deps.heat.tier];
    if (scale <= 0) return;
    addHeat(this.deps.heat, vision.sightHeatPerSecond * scale * dt);
  }

  /* ----------------------------- actions --------------------------- */

  performSpray(): void {
    this.sprayCooldown = tuning.granny.spray.cooldown;
    const cat = this.deps.getCat();
    this.deps.bus.emit('cat:sprayed', { position: clone(cat.position) });
  }

  performCatch(): void {
    const cat = this.deps.getCat();
    this.deps.bus.emit('cat:caught', { by: 'granny', position: clone(cat.position) });
  }

  /** Called by the game after a catch resolves, to put her back to work. */
  resetAfterCatch(): void {
    this.endCarry();
    this.machine.transitionTo(this, 'routine');
    this.memory.lastKnownPosition = null;
    clearNoiseMemory(this.memory);
    this.memory.checkedHideSpots.clear();
    this.sprintTime = 0;
    this.exhausted = false;
    this.pendingStimulus = false;
    this.investigateTarget = null;
    this.investigateAnchor = null;
    this.searchTarget = null;
    this.petCooldown = tuning.granny.pet.cooldownSeconds;
    this.routine.reset();
  }

  /* ------------------------- carrying the cat ---------------------- */

  /**
   * Start walking a caught cat back to `destination`.
   *
   * DESIGN.md's fail state is "granny scruffs the cat and plops it in its bed".
   * She used to simply stand on top of it while the cat teleported home, which
   * read as the game glitching rather than as losing.
   *
   * Deliberately NOT a state in the transition table: nothing may interrupt a
   * carry, and a `from: '*'` transition sneaking her out of it mid-walk is
   * exactly the class of bug a flat FSM makes easy to write.
   */
  beginCarry(destination: Vec3): void {
    this.carryDestination = clone(destination);
    this.carryElapsed = 0;
    this.carryBestDistance = Infinity;
    this.carryStuckFor = 0;
    this.carrySide = 1;
    this.moveTarget = null;
  }

  endCarry(): void {
    this.carryDestination = null;
    this.carryElapsed = 0;
  }

  get isCarryingCat(): boolean {
    return this.carryDestination !== null;
  }

  /**
   * True once the cat is home, or once she has spent too long failing to get
   * there. She has no pathfinder, and respawning at the bed is a contract the
   * level depends on, so the timeout has to exist.
   */
  get carryFinished(): boolean {
    if (!this.carryDestination) return true;
    if (this.carryElapsed >= tuning.granny.carry.timeoutSeconds) return true;
    return distanceXZ(this.position, this.carryDestination) <= tuning.granny.carry.placeRadius;
  }

  /** Where the scruffed cat dangles. Local -z is forward, as everywhere else. */
  get carryPoint(): Vec3 {
    const [ox, oy, oz] = tuning.granny.carry.holdOffset;
    const sin = Math.sin(this.yaw);
    const cos = Math.cos(this.yaw);
    return vec3(
      this.position.x + ox * cos + oz * sin,
      this.position.y + oy,
      this.position.z - ox * sin + oz * cos,
    );
  }

  private stepCarry(dt: number): void {
    this.carryElapsed += dt;
    const destination = this.carryDestination;
    if (!destination) return;

    const carry = tuning.granny.carry;
    const remaining = distanceXZ(this.position, destination);
    if (remaining < this.carryBestDistance - carry.progressEpsilon) {
      this.carryBestDistance = remaining;
      this.carryStuckFor = 0;
    } else {
      this.carryStuckFor += dt;
      // One sidestep attempt is over: try straight again, from the other side.
      if (this.carryStuckFor >= carry.sidestepAfterSeconds + carry.sidestepSeconds) {
        this.carryStuckFor = 0;
        this.carrySide = -this.carrySide;
      }
    }

    this.moveTarget = this.carryFinished ? null : this.carrySteerTarget(destination, remaining);
    this.applyMovement(dt, carry.speed);
    this.updateView();
  }

  /**
   * Where she actually walks while carrying.
   *
   * Straight at the bed until she stops making progress, then a strafe for a
   * moment before trying again - a bug-algorithm detour, not a path. She has no
   * navmesh, the bed sits in a gap between the couch and the side table, and a
   * granny leaning into the furniture with a cat under her arm is the worst
   * possible cutaway. The carry timeout is still there as the backstop.
   */
  private carrySteerTarget(destination: Vec3, remaining: number): Vec3 {
    const carry = tuning.granny.carry;
    if (this.carryStuckFor < carry.sidestepAfterSeconds) return destination;

    const length = remaining || 1;
    const dx = (destination.x - this.position.x) / length;
    const dz = (destination.z - this.position.z) / length;
    return vec3(
      this.position.x - dz * carry.sidestepDistance * this.carrySide,
      destination.y,
      this.position.z + dx * carry.sidestepDistance * this.carrySide,
    );
  }

  /* ---------------------------- petting ---------------------------- */

  /** Whether a fuss is off cooldown. The tier check lives in the transition. */
  get wantsToPet(): boolean {
    const cat = this.deps.getCat();
    return (
      this.petCooldown <= 0 &&
      this.canSeeCat &&
      cat.hideSpotId === null &&
      this.catDistance <= tuning.granny.pet.noticeRange
    );
  }

  beginPet(): void {
    this.petElapsed = 0;
    this.petDwell = 0;
  }

  /**
   * One step of ambling over for a fuss. Returns true once it is over, either
   * because she got there or because the cat wandered off.
   *
   * She gives up rather than trailing after a cat that has better things to do -
   * a granny who follows you around wanting a cuddle is the same bug as a granny
   * who follows you around wanting to spray you.
   */
  stepPet(dt: number): boolean {
    const pet = tuning.granny.pet;
    this.petElapsed += dt;
    const cat = this.deps.getCat();

    if (this.petElapsed >= pet.giveUpSeconds || !this.canSeeCat) {
      this.petCooldown = pet.cooldownSeconds;
      return true;
    }

    if (distanceXZ(this.position, cat.position) > pet.reachDistance) {
      this.moveTarget = clone(cat.position);
      this.petDwell = 0;
      return false;
    }

    // Close enough. Stand still and have a proper fuss.
    this.moveTarget = null;
    this.petDwell += dt;
    if (this.petDwell < pet.durationSeconds) return false;

    this.petCooldown = pet.cooldownSeconds;
    this.deps.bus.emit('granny:petted', {
      position: clone(cat.position),
      cozyPoints: pet.cozyPoints,
    });
    return true;
  }

  /* ------------------------------- view ---------------------------- */

  private updateView(): void {
    this.object.position.set(this.position.x, this.position.y, this.position.z);
    this.object.rotation.y = this.yaw;

    const profile = this.activeProfile;
    const blind = profile.visionFovScale <= 0;

    this.visionCone.visible = !blind;
    this.visionCone.position.set(this.position.x, this.position.y + 0.02, this.position.z);
    this.visionCone.rotation.y = this.yaw;
    // Scale the cone to the activity's actual range so what you see is what she
    // can see. A cone that lies about its range would be worse than none at all.
    this.visionCone.scale.setScalar(Math.max(0.001, profile.visionRangeScale));

    this.bubble.visible = !blind;
    this.bubble.position.set(this.position.x, this.position.y + 0.015, this.position.z);

    this.coneMaterial.color.set(CONE_COLORS[this.tier]);
    this.coneMaterial.opacity = this.tier === 'standby' ? 0.13 : 0.22;
  }

  /**
   * Draws the vision cone through geometry, for Cat Instinct.
   *
   * The cone itself is unchanged: it is already scaled to the activity's real
   * range and hidden when the activity is blind, so a child reading it through a
   * wall is reading the truth. Inventing a cone for a blind granny, or widening
   * it here, would turn the game's most important teaching aid into a lie.
   */
  setConeSeeThrough(seeThrough: boolean): void {
    this.coneMaterial.depthTest = !seeThrough;
    this.visionCone.renderOrder = seeThrough ? 9 : 0;
  }

  dispose(): void {
    this.unsubscribeNoise();
    this.deps.scene.remove(this.object);
    this.deps.scene.remove(this.visionCone);
    this.deps.scene.remove(this.bubble);
    this.visionCone.geometry.dispose();
    this.coneMaterial.dispose();
  }

  /* ------------------- accessors used by the FSM ------------------- */

  get investigating(): Trace | null {
    return this.investigateTarget;
  }

  /**
   * Commits to one place to go and look, and snapshots it.
   *
   * The snapshot is the whole point. This used to be a live getter reading
   * `memory.lastNoisePosition`, which the cat's own footsteps rewrote every
   * fixed step - so "walk to where I heard that" quietly became "walk to
   * wherever the cat is now", at every tier, with no way out of the loop.
   */
  pickInvestigateTarget(): void {
    this.investigateElapsed = 0;

    // A heard noise takes priority over a trace: it is more recent information.
    if (this.memory.lastNoisePosition) {
      this.investigateTarget = null;
      this.investigateAnchor = clone(this.memory.lastNoisePosition);
      return;
    }

    const trace = nearestUnresolvedTrace(this.deps.traces, this.position) ?? null;
    this.investigateTarget = trace;
    if (trace) {
      this.investigateAnchor = clone(trace.position);
      return;
    }
    this.investigateAnchor = this.memory.lastKnownPosition
      ? clone(this.memory.lastKnownPosition)
      : null;
  }

  /** Where "investigate" should walk to. Fixed until she resolves or gives up. */
  get investigatePosition(): Vec3 | null {
    return this.investigateAnchor;
  }

  /**
   * True once she has spent too long failing to reach what she is investigating.
   * Same no-pathfinder backstop as the routine and search timeouts.
   */
  get investigateUnreachable(): boolean {
    return this.investigateElapsed >= tuning.granny.navigation.investigateTimeoutSeconds;
  }

  tidyCurrentTrace(dt: number): boolean {
    if (!this.investigateTarget) return false;
    return tidyTrace(this.investigateTarget, dt);
  }

  /**
   * She looked, and there was nothing there.
   *
   * Forgetting the sighting as well as the noise is the point: an anchor derived
   * from `lastKnownPosition` is regenerated the instant it is cleared, so she
   * stood on the spot re-deciding to look at the same empty patch of rug until
   * the memory aged out twelve seconds later. Having checked somewhere is
   * exactly the cue to go and check somewhere else.
   */
  clearInvestigateTarget(): void {
    this.investigateTarget = null;
    this.investigateAnchor = null;
    clearNoiseMemory(this.memory);
    this.memory.lastKnownPosition = null;
    this.memory.lastKnownAge = 0;
  }

  pickSearchTarget(): void {
    this.searchTarget =
      nextHideSpotToCheck(this.memory, this.position, this.deps.hideSpots) ?? null;
    this.searchTargetElapsed = 0;
  }

  /**
   * True once she has spent too long failing to reach the spot she is checking.
   *
   * Same root cause as the routine stall: no pathfinder, so a hide spot tucked
   * behind a bookshelf is unreachable, and a spot is only marked checked on
   * ARRIVAL. Without this she leans into the shelf until the player walks over
   * and rescues her.
   */
  get searchTargetUnreachable(): boolean {
    return this.searchTargetElapsed >= tuning.granny.navigation.searchReachTimeoutSeconds;
  }

  get searchPosition(): Vec3 | null {
    return this.searchTarget?.position ?? this.memory.lastKnownPosition ?? null;
  }

  markSearchTargetChecked(): void {
    if (this.searchTarget) markHideSpotChecked(this.memory, this.searchTarget.id);
    this.searchTarget = null;
  }

  get hasSearchTarget(): boolean {
    return this.searchTarget !== null;
  }

  /**
   * Somewhere to walk when she is hunting with nothing left to check.
   *
   * Advances the routine when she gets there, so she works around the room
   * rather than orbiting one spot. Reuses the anchors precisely because they are
   * already proven reachable - see the navigation timeouts in tuning.json.
   */
  get patrolTarget(): Vec3 {
    const anchor = this.routine.anchor;
    if (distanceXZ(this.position, anchor) <= tuning.granny.investigate.arriveRadius) {
      this.routine.advance();
    }
    return this.routine.anchor;
  }

  /** Read-only routine state, so a sim test can prove she is not stuck. */
  get routineDebug(): { phase: string; anchor: Vec3; unreachableAnchors: number } {
    return {
      phase: this.routine.phase,
      anchor: this.routine.anchor,
      unreachableAnchors: this.routine.unreachableAnchorCount,
    };
  }

  get catLastKnown(): Vec3 | null {
    return this.memory.lastKnownPosition;
  }

  get catSnapshot(): CatSnapshot {
    return this.deps.getCat();
  }

  clearExhaustion(): void {
    this.exhausted = false;
    this.sprintTime = 0;
  }
}

/* ------------------------------------------------------------------ */
/* States                                                             */
/* ------------------------------------------------------------------ */

function buildStates(): State<Granny>[] {
  const g = tuning.granny;

  return [
    {
      name: 'routine',
      enter: (granny) => granny.routine.resume(),
      update: (granny, dt) => {
        const target = granny.routine.step(granny.position, dt, g.investigate.arriveRadius);
        granny.moveTarget = target;
      },
      exit: (granny) => granny.routine.pause(),
    },

    {
      name: 'pet',
      // Calm granny wandering over for a fuss. Not a stealth beat at all - it is
      // there so Standby reads as its own state rather than as "she has not
      // noticed you yet", which is what every tier looked like from the player's
      // side when she trailed them around the room at every heat level.
      enter: (granny) => granny.beginPet(),
      update: (granny, dt) => {
        if (granny.stepPet(dt)) granny.machine.transitionTo(granny, 'routine');
      },
    },

    {
      name: 'alert',
      // The "?" beat. She stops dead and looks around. This pause is the
      // player's warning shot and the single most important piece of
      // telegraphing in the game - never shorten it for pacing.
      enter: (granny) => {
        granny.moveTarget = null;
        granny.beginAlert();
        granny.pickInvestigateTarget();
      },
      update: (granny) => {
        granny.moveTarget = null;
      },
    },

    {
      name: 'investigate',
      update: (granny, dt) => {
        const target = granny.investigatePosition;
        if (!target) {
          granny.moveTarget = null;
          return;
        }
        if (distanceXZ(granny.position, target) > g.investigate.arriveRadius) {
          // Cannot get there - a noise heard from behind the couch, say. Give up
          // rather than lean on the furniture until the player rescues her.
          if (granny.investigateUnreachable) {
            granny.clearInvestigateTarget();
            granny.pickInvestigateTarget();
            granny.moveTarget = null;
            return;
          }
          granny.moveTarget = target;
          return;
        }
        // Arrived: tidy up whatever she found, then forget about it.
        granny.moveTarget = null;
        const done = granny.tidyCurrentTrace(dt);
        if (done || !granny.investigating) {
          granny.clearInvestigateTarget();
          granny.pickInvestigateTarget();
        }
      },
    },

    {
      name: 'search',
      // Hunting with no idea where you are: she works through hiding spots.
      // Running out of unchecked spots is the player's reward for hiding well.
      enter: (granny) => granny.pickSearchTarget(),
      update: (granny) => {
        const target = granny.searchPosition;
        if (!target) {
          // Nothing left to check and still angry. DESIGN.md's Hunting tier says
          // she PATROLS - and standing perfectly still in the middle of the room
          // until the player wanders past was the single most unconvincing thing
          // she did. Her routine anchors are known-reachable points, so they make
          // a free patrol route with no navmesh and no new level data.
          granny.moveTarget = granny.patrolTarget;
          return;
        }
        if (distanceXZ(granny.position, target) > g.investigate.arriveRadius) {
          if (granny.searchTargetUnreachable) {
            // Cannot get there. Count it as checked and head for the next one, or
            // she leans into the furniture until the player rescues her. Note she
            // is given the new target immediately rather than a null: an idle
            // step here would stop her burning frenzy stamina, and the stamina
            // window is the player's guaranteed escape.
            granny.markSearchTargetChecked();
            granny.pickSearchTarget();
            granny.moveTarget = granny.searchPosition;
            return;
          }
          granny.moveTarget = target;
          return;
        }
        granny.moveTarget = null;
        granny.markSearchTargetChecked();
        granny.pickSearchTarget();
      },
    },

    {
      name: 'chase',
      update: (granny) => {
        const target = granny.canSeeCat ? granny.catSnapshot.position : granny.catLastKnown;
        granny.moveTarget = target ? clone(target) : null;
      },
    },

    {
      name: 'spray',
      // Wind-up is the dodge window. Telegraph it hard in animation and audio.
      update: (granny) => {
        granny.moveTarget = null;
        if (granny.machine.timeInState >= g.spray.windUpTime) {
          granny.performSpray();
          granny.machine.transitionTo(granny, 'chase');
        }
      },
    },

    {
      name: 'catch',
      update: (granny) => {
        granny.moveTarget = null;
        if (granny.machine.timeInState >= g.catch.windUpTime) {
          granny.performCatch();
          granny.machine.transitionTo(granny, 'routine');
        }
      },
    },

    {
      name: 'recover',
      // Hands on knees, wheezing. Funniest animation in the game and a fair
      // escape window for a 6-year-old.
      update: (granny) => {
        granny.moveTarget = null;
        if (granny.machine.timeInState >= g.frenzy.wheezeSeconds) {
          granny.clearExhaustion();
          granny.machine.transitionTo(granny, granny.spotted ? 'chase' : 'search');
        }
      },
    },
  ];
}

/* ------------------------------------------------------------------ */
/* Transition table - read this to understand her behavior            */
/* ------------------------------------------------------------------ */

function buildTransitions(): Transition<Granny>[] {
  const g = tuning.granny;

  return [
    // Catching wins over everything. If she is on top of you, you are caught.
    {
      from: '*',
      to: 'catch',
      priority: 100,
      when: (granny) =>
        granny.isAggressive &&
        granny.hasFix &&
        granny.catDistance <= g.catch.range &&
        granny.machine.current !== 'catch',
    },

    // Exhaustion beats chasing but not catching - she gets her hands on you
    // first if she is close enough, otherwise she has to stop and breathe.
    {
      from: '*',
      to: 'recover',
      priority: 95,
      when: (granny) => granny.exhausted && granny.machine.current !== 'recover',
    },

    {
      from: '*',
      to: 'spray',
      priority: 90,
      when: (granny) =>
        granny.isAggressive &&
        granny.hasFix &&
        granny.canSpray &&
        granny.catDistance <= g.spray.range &&
        granny.machine.current !== 'spray' &&
        granny.machine.current !== 'catch',
    },

    // Aggressive and she knows where you are. No reaction-delay gate here: by
    // Hunting she has already been pulled off her routine by priority 65, so the
    // interrupt delay her activity buys has been spent. Requiring it again meant
    // the chase hung on a `pendingStimulus` flag that nothing ever cleared.
    //
    // The two exclusions are not tidiness, they are the fix for a real and very
    // visible bug. `catch` and `spray` are WIND-UPS: they do their work after a
    // timer, and priority 100 and 90 both refuse to re-enter their own state, so
    // this rule was the highest one left that could fire from inside them. She
    // therefore flipped catch->chase->catch every step, her wind-up timer reset
    // every step, and she never actually caught anything - she just stood on the
    // cat forever. The spray telegraph, the player's entire dodge window, was
    // being cancelled one step after it started for the same reason.
    {
      from: '*',
      to: 'chase',
      priority: 80,
      when: (granny) =>
        granny.isAggressive &&
        granny.hasFix &&
        granny.machine.current !== 'catch' &&
        granny.machine.current !== 'spray',
    },

    // Lost sight mid-chase: fall back to working through hiding spots.
    {
      from: 'chase',
      to: 'search',
      priority: 70,
      when: (granny) => !granny.hasFix && granny.isAggressive,
    },

    // High heat alone pulls her off her routine even with no fresh stimulus. A
    // granny who is Hunting-angry is not going to carry on watering her plants,
    // and without this she could sit at heat 70 doing nothing because the noise
    // that alarmed her has already been forgotten.
    {
      from: 'routine',
      to: 'alert',
      priority: 65,
      when: (granny) => granny.isAggressive,
    },

    // Suspicious with no fresh stimulus: she stops and has a look around every
    // so often, then goes back to her day. DESIGN.md's tier table says exactly
    // that - "pauses, ? bubble, looks around, investigates, resumes" - but until
    // now it only happened when something re-alarmed her, so a suspicious granny
    // was indistinguishable from a calm one and just kept watering her plants.
    // The dwell gate is what keeps it "every so often" instead of a thrash
    // between routine and alert.
    {
      from: 'routine',
      to: 'alert',
      priority: 62,
      when: (granny) =>
        granny.tier === 'suspicious' &&
        granny.machine.timeInState >= g.investigate.suspiciousCheckSeconds,
    },

    // A stimulus while calm produces the "?" pause rather than an instant chase.
    // `readyToReact` is now the whole condition, because a stimulus is only
    // raised by something she should actually care about at her current tier -
    // see the investigateLoudness table and the justSpotted gate in step().
    {
      from: 'routine',
      to: 'alert',
      priority: 60,
      when: (granny) => granny.readyToReact,
    },

    // Anything at all happening ends the fuss and she goes back to her day.
    {
      from: 'pet',
      to: 'routine',
      priority: 58,
      when: (granny) => granny.tier !== 'standby' || granny.readyToReact,
    },

    // Calm, and there is a cat right there. See the `pet` state.
    {
      from: 'routine',
      to: 'pet',
      priority: 55,
      when: (granny) => granny.tier === 'standby' && granny.wantsToPet,
    },

    {
      from: 'alert',
      to: 'investigate',
      priority: 50,
      when: (granny) => granny.machine.timeInState >= g.investigate.lookAroundSeconds,
    },

    // Nothing left to check: give up and go back to her day.
    {
      from: 'search',
      to: 'routine',
      priority: 40,
      when: (granny) => !granny.hasSearchTarget && !granny.isAggressive,
    },

    // Aggressive with nothing left to investigate: start working the hiding
    // spots. Without this she would stand still in `investigate` forever, since
    // the return-to-routine transition requires her to have calmed down first.
    {
      from: 'investigate',
      to: 'search',
      priority: 45,
      when: (granny) => granny.isAggressive && granny.investigatePosition === null,
    },

    {
      from: 'investigate',
      to: 'routine',
      priority: 35,
      when: (granny) => granny.investigatePosition === null && granny.tier === 'standby',
    },

    // Catch-all: fully calm and nothing to see means back to the routine.
    {
      from: '*',
      to: 'routine',
      priority: 10,
      when: (granny) =>
        granny.tier === 'standby' &&
        !granny.spotted &&
        !granny.exhausted &&
        granny.machine.current !== 'routine' &&
        granny.machine.current !== 'catch' &&
        granny.machine.current !== 'spray' &&
        // `pet` ends itself; yanking her out of it here would cut the fuss short
        // the instant she arrived, since a calm granny always satisfies this.
        granny.machine.current !== 'pet' &&
        granny.investigatePosition === null,
    },
  ];
}

/* ------------------------------------------------------------------ */
/* View helpers                                                       */
/* ------------------------------------------------------------------ */

/**
 * Substituted for the routine profile the moment she is off her schedule.
 *
 * Fully alert on purpose: once she is hunting she is not still half-deaf from
 * the television, which is why the VR window closes the instant she is alarmed.
 */
const ALERTED_PROFILE: ActivityProfile = {
  id: 'alerted',
  label: 'Alerted',
  visionFovScale: 1,
  visionRangeScale: 1,
  hearingScale: 1,
  stationary: false,
  facing: 'anchor',
  interruptDelay: 0,
};

/** She is crouched over the cat making a fuss of it, not on guard. */
const PETTING_PROFILE: ActivityProfile = {
  ...ALERTED_PROFILE,
  id: 'petting',
  label: 'Saying hello',
  interruptDelay: 0.4,
};

const CONE_COLORS: Record<HeatTier, string> = {
  standby: '#ffe066',
  suspicious: '#ffa94d',
  hunting: '#ff6b35',
  frenzy: '#e03131',
};

/**
 * Flat floor sector showing exactly what she can see.
 *
 * Built directly in the XZ plane with angle 0 pointing along -Z (Three's
 * forward), so setting `rotation.y = yaw` aligns it with her facing with no
 * extra transform juggling.
 */
function buildVisionCone(
  fovDeg: number,
  range: number,
): { mesh: THREE.Mesh; material: THREE.MeshBasicMaterial } {
  const segments = 24;
  const half = (fovDeg * DEG_TO_RAD) / 2;

  const positions: number[] = [0, 0, 0];
  for (let i = 0; i <= segments; i++) {
    const angle = -half + (i / segments) * half * 2;
    positions.push(-Math.sin(angle) * range, 0, -Math.cos(angle) * range);
  }

  const indices: number[] = [];
  for (let i = 1; i <= segments; i++) indices.push(0, i, i + 1);

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);

  const material = new THREE.MeshBasicMaterial({
    color: CONE_COLORS.standby,
    transparent: true,
    opacity: 0.13,
    depthWrite: false,
    side: THREE.DoubleSide,
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'granny:visionCone';
  mesh.renderOrder = 1;
  return { mesh, material };
}

/**
 * A face for the placeholder capsule: two eyes and a nose, merged.
 *
 * Deliberately ONE mesh built from one interleaved geometry rather than three
 * children, because three meshes would be three draw calls for one piece of
 * information. No outline either - it is interior detail, exactly like the cat's
 * eyes.
 *
 * Forward is -z, matching forwardFromYaw() and the rest of the project.
 */
function buildFaceMarker(): THREE.Mesh {
  const face = tuning.granny.presentation.face;
  const positions: number[] = [];
  const indices: number[] = [];

  const addDisc = (cx: number, cy: number, radius: number, segments: number): void => {
    const base = positions.length / 3;
    positions.push(cx, cy, face.depth);
    for (let i = 0; i < segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      positions.push(cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius, face.depth);
    }
    for (let i = 0; i < segments; i++) {
      indices.push(base, base + 1 + ((i + 1) % segments), base + 1 + i);
    }
  };

  addDisc(-face.eyeSpacing / 2, face.eyeHeight, face.eyeRadius, 10);
  addDisc(face.eyeSpacing / 2, face.eyeHeight, face.eyeRadius, 10);
  addDisc(0, face.noseHeight, face.noseRadius, 8);

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();

  const mesh = new THREE.Mesh(
    geometry,
    new THREE.MeshBasicMaterial({ color: tuning.render.outlineColor, side: THREE.DoubleSide }),
  );
  mesh.name = 'granny:face';
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  return mesh;
}

/** Thin ring for the 360-degree personal-space bubble. */
function buildPersonalSpaceRing(radius: number): THREE.Mesh {
  const geometry = new THREE.RingGeometry(radius - 0.04, radius, 28);
  geometry.rotateX(-Math.PI / 2);
  const material = new THREE.MeshBasicMaterial({
    color: '#ffffff',
    transparent: true,
    opacity: 0.12,
    depthWrite: false,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'granny:personalSpace';
  mesh.renderOrder = 1;
  return mesh;
}
